import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';
import type { TreatmentCategory } from '@/types';

interface CategorySelectProps {
  value: string | undefined;
  activeCategories: TreatmentCategory[];
  onChange: (newId: string | undefined) => void | Promise<void>;
  placeholderKey?: string;
  clearLabelKey?: string;
  disabled?: boolean;
  triggerClassName?: string;
}

const SENTINEL = '__none__';

export function CategorySelect({
  value,
  activeCategories,
  onChange,
  placeholderKey = 'dashboard.admin.helps.linksView.treatmentCategoryPlaceholder',
  clearLabelKey = 'dashboard.admin.helps.linksView.treatmentCategoryClear',
  disabled = false,
  triggerClassName,
}: CategorySelectProps) {
  const { t, dir } = useLanguage();
  // Match the inline amount-pill style used for «المتاح / المخصص» (e.g.
  // `bg-green-50 text-green-700 px-2 py-0.5 rounded-full`). No chevron icon
  // (the underlying svg is hidden via `[&>svg]:hidden`). Yellow tint when
  // classified, neutral when not.
  const setPillClass = 'h-auto w-fit inline-flex items-center gap-1 px-3 py-0.5 text-xs font-medium rounded-full bg-yellow-50 hover:bg-yellow-100 text-yellow-800 border-0 [&>svg]:hidden focus:outline-none focus:ring-2 focus:ring-yellow-300';
  const unsetPillClass = 'h-auto w-fit inline-flex items-center gap-1 px-3 py-0.5 text-xs font-medium rounded-full bg-gray-50 hover:bg-gray-100 text-muted-foreground border-0 [&>svg]:hidden focus:outline-none focus:ring-2 focus:ring-gray-300';
  const effectiveTrigger = triggerClassName ?? (value ? setPillClass : unsetPillClass);
  return (
    <Select
      dir={dir}
      value={value ?? SENTINEL}
      disabled={disabled}
      onValueChange={(v) => onChange(v === SENTINEL ? undefined : v)}
    >
      <SelectTrigger className={effectiveTrigger} dir={dir}>
        <SelectValue placeholder={t(placeholderKey)} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={SENTINEL} className="text-muted-foreground">
          {t(clearLabelKey)}
        </SelectItem>
        {activeCategories.map(c => (
          <SelectItem key={c.id} value={c.id}>
            {dir === 'rtl' ? (c.nameAr || c.name) : c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
